import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageWrapper from "../../components/layout/PageWrapper";
import DataTable from "../../components/tables/DataTable";
import Button from "../../components/forms/Button";
import BaseModal from "../../components/modals/BaseModal";
import ConfirmModal from "../../components/modals/ConfirmModal";
import Input from "../../components/forms/Input";
import Select from "../../components/forms/Select";
import Toast from "../../components/ui/Toast";
import { useModal } from "../../hooks/useModal";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import { axiosPrivate } from "../../api/axios";
import { Plus, Calendar } from "lucide-react";
import { motion } from "framer-motion";

// Helper functions to map backend data to frontend format
const TEAM_LABELS = {
  high_potit: "High Potit",
  backend: "Backend",
  frontend: "Frontend",
  tester: "Tester",
  ra: "Requirement Analyst",
  mobile_app: "Mobile App",
  uiux: "UI/UX",
};

const formatTeam = (team) => TEAM_LABELS[team] || team;

const transformEmployeeForDisplay = (employee) => {
  const id = employee._id?.toString() || employee.id?.toString();
  return {
    id,
    fullName: employee.fullNameEn,
    fullNameArabic: employee.fullNameAr,
    employeeCode: employee.employeeCode,
    fingerprintCode: employee.fingerprintCode,
    email: employee.email,
    phone: employee.phone,
    jobPosition:
      employee.jobPosition === "software_engineer"
        ? "Software Engineer"
        : employee.jobPosition,
    teamsDisplay: (employee.teams || []).length
      ? employee.teams
          .map((t) => `${formatTeam(t.team)} (${formatTitle(t.title)})`)
          .join(" · ")
      : formatTitle(employee.title) || "",
    branch: employee.branch,
    status: employee.status,
    vacationBalance: employee.vacationBalance || 0,
    // Keep original data for editing
    _original: employee,
  };
};

const formatTitle = (title) => {
  const titleMap = {
    team_leader: "Team Lead",
    senior: "Senior",
    mid_level: "Mid Level",
    junior: "Junior",
    intern: "Intern",
  };
  return titleMap[title] || title;
};

const Employees = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const employee = auth?.employee;

  const [employeeList, setEmployeeList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isOpen, openModal, closeModal, modalData } = useModal();
  const [isVacationModalOpen, setIsVacationModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [vacationDays, setVacationDays] = useState({});
  const [formData, setFormData] = useState({
    fullNameEn: "",
    fullNameAr: "",
    nickname: "",
    discordId: "",
    discordUsername: "",
    email: "",
    employeeCode: "",
    fingerprintCode: "",
    phone: "",
    branch: "Main Branch",
    jobPosition: "software_engineer",
    teams: [{ team: "", title: "" }],
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({
    isVisible: false,
    message: "",
    type: "success",
  });

  // Redirect if not high_potit team
  useEffect(() => {
    if (employee && !isAdmin(employee)) {
      navigate("/main/dashboard");
    }
  }, [employee, navigate]);

  // Fetch employees on component mount
  useEffect(() => {
    fetchEmployees();
  }, []);

  // Discord members (from the attendance bot) for the "Discord user" dropdown.
  const [discordMembers, setDiscordMembers] = useState([]);
  const [discordError, setDiscordError] = useState("");
  useEffect(() => {
    axiosPrivate
      .get("/api/presence/members")
      .then((res) => setDiscordMembers(res.data?.data || []))
      .catch((err) =>
        setDiscordError(err.response?.data?.message || "Could not load Discord users"),
      );
  }, []);

  const NO_DISCORD = "__none__";
  const handleDiscordChange = (e) => {
    const id = e.target.value === NO_DISCORD ? "" : e.target.value;
    const member = discordMembers.find((m) => m.userId === id);
    setFormData((prev) => ({
      ...prev,
      discordId: id,
      discordUsername: member ? member.username || member.displayName : "",
    }));
  };
  // Keep an already-linked user selectable even if the bot list doesn't have them.
  const discordOptions = [
    { value: NO_DISCORD, label: "— Not linked —" },
    ...(formData.discordId && !discordMembers.some((m) => m.userId === formData.discordId)
      ? [{ value: formData.discordId, label: `@${formData.discordUsername || formData.discordId}` }]
      : []),
    ...discordMembers.map((m) => ({
      value: m.userId,
      label: m.username && m.username !== m.displayName ? `${m.displayName} (@${m.username})` : m.displayName,
    })),
  ];

  // Initialize vacation days when employees are loaded
  useEffect(() => {
    const initialDays = {};
    employeeList.forEach((emp) => {
      initialDays[emp.id] = emp.vacationBalance || 0;
    });
    setVacationDays(initialDays);
  }, [employeeList]);

  const fetchEmployees = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosPrivate.get("/api/employees");
      const transformed = response.data.map(transformEmployeeForDisplay);
      // Sort by Employee Code in ascending order (numeric sort)
      const sorted = transformed.sort((a, b) => {
        const codeA = parseInt(a.employeeCode) || 0;
        const codeB = parseInt(b.employeeCode) || 0;
        return codeA - codeB;
      });
      // Hard delete removes documents, so just show all returned employees
      setEmployeeList(sorted);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch employees");
      console.error("Error fetching employees:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { key: "fullName", header: "Full Name" },
    { key: "fullNameArabic", header: "Full Name (Arabic)" },
    { key: "employeeCode", header: "Employee Code" },
    { key: "fingerprintCode", header: "Fingerprint Code" },
    { key: "email", header: "Email" },
    { key: "phone", header: "Phone" },
    { key: "jobPosition", header: "Job Position" },
    { key: "teamsDisplay", header: "Teams" },
    { key: "branch", header: "Branch" },
  ];

  const handleEdit = (employee) => {
    const original = employee._original || employee;
    setFormData({
      fullNameEn: original.fullNameEn || original.fullName || "",
      fullNameAr: original.fullNameAr || original.fullNameArabic || "",
      nickname: original.nickname || "",
      discordId: original.discordId || "",
      discordUsername: original.discordUsername || "",
      email: original.email || "",
      employeeCode: original.employeeCode || "",
      fingerprintCode: original.fingerprintCode || "",
      phone: original.phone || "",
      branch: original.branch || "",
      jobPosition: original.jobPosition || "software_engineer",
      // Prefer teams array; fall back if only legacy team/title present
      teams:
        Array.isArray(original.teams) && original.teams.length > 0
          ? original.teams.map((t) => ({ team: t.team, title: t.title }))
          : [{ team: original.team || "", title: original.title || "" }],
    });
    setFormErrors({});
    openModal(employee);
  };

  const handleDelete = (employee) => {
    console.log("handleDelete called with employee:", employee);
    console.log("Employee ID:", employee.id);
    console.log("Employee _original:", employee._original);

    setEmployeeToDelete(employee);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!employeeToDelete) return;

    try {
      console.log("Attempting to delete employee:", employeeToDelete.id);
      const response = await axiosPrivate.delete(
        `/api/employees/${employeeToDelete.id}`,
      );
      console.log("Delete response:", response.data);

      setIsDeleteModalOpen(false);
      setEmployeeToDelete(null);
      showToast(
        response.data?.message || "Employee deleted successfully",
        "success",
      );

      // Refresh list immediately
      await fetchEmployees();
    } catch (err) {
      console.error("Full error object:", err);
      console.error("Error response:", err.response);

      let errorMessage = "Failed to deactivate employee";

      if (err.response) {
        if (err.response.status === 403) {
          errorMessage =
            err.response.data?.message ||
            "You do not have permission to delete employees. Admin access required.";
        } else if (err.response.status === 404) {
          errorMessage = "Employee not found";
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data?.details) {
          errorMessage = err.response.data.details.join(", ");
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      showToast(errorMessage, "error");

      // Close modal on 403 or 404 errors
      if (err.response?.status === 403 || err.response?.status === 404) {
        setIsDeleteModalOpen(false);
        setEmployeeToDelete(null);
      }
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setEmployeeToDelete(null);
  };

  const showToast = (message, type = "success") => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, isVisible: false });
  };

  const handleAddNew = () => {
    setFormData({
      fullNameEn: "",
      fullNameAr: "",
      nickname: "",
      discordId: "",
      discordUsername: "",
      email: "",
      employeeCode: "",
      fingerprintCode: "",
      phone: "",
      branch: "Main Branch",
      jobPosition: "software_engineer",
      teams: [{ team: "", title: "" }],
    });
    setFormErrors({});
    openModal(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTeamEntryChange = (idx, field, value) => {
    setFormData((prev) => {
      const newTeams = [...prev.teams];
      newTeams[idx] = { ...newTeams[idx], [field]: value };
      return { ...prev, teams: newTeams };
    });
    if (formErrors.teams) {
      setFormErrors((prev) => {
        const e = { ...prev };
        delete e.teams;
        return e;
      });
    }
  };

  const handleAddTeam = () => {
    setFormData((prev) => ({
      ...prev,
      teams: [...prev.teams, { team: "", title: "" }],
    }));
  };

  const handleRemoveTeam = (idx) => {
    setFormData((prev) => ({
      ...prev,
      teams: prev.teams.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormErrors({});

    // Client-side teams validation
    const teamsErrors = formData.teams.some((t) => !t.team || !t.title);
    if (teamsErrors || formData.teams.length === 0) {
      setFormErrors({
        teams: "Each team entry must have both a team and a title selected.",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        jobPosition: "software_engineer", // Always software_engineer
      };

      if (modalData) {
        // Update existing employee - exclude email as it's forbidden
        const { email, ...updatePayload } = payload;
        await axiosPrivate.put(`/api/employees/${modalData.id}`, updatePayload);
        showToast("Employee updated successfully", "success");
      } else {
        // Create new employee
        await axiosPrivate.post("/api/employees", payload);
        showToast("Employee added successfully", "success");
      }

      closeModal();
      await fetchEmployees(); // Refresh list
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.details) {
        // Validation errors from backend
        const errors = {};
        err.response.data.details.forEach((detail) => {
          const field = detail.match(/"([^"]+)"/)?.[1];
          if (field) {
            errors[field] = detail;
          }
        });
        setFormErrors(errors);
        showToast("Please fix the validation errors", "error");
      } else {
        const errorMsg =
          err.response?.data?.message || "Failed to save employee";
        setFormErrors({ submit: errorMsg });
        showToast(errorMsg, "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVacationDaysChange = (employeeId, days) => {
    setVacationDays((prev) => ({
      ...prev,
      [employeeId]: parseInt(days) || 0,
    }));
  };

  const handleSaveVacationDays = async () => {
    try {
      // Update vacation balance for each employee
      const updates = Object.entries(vacationDays).map(([employeeId, days]) => {
        const employee = employeeList.find((emp) => emp.id === employeeId);
        if (employee && employee.vacationBalance !== days) {
          return axiosPrivate.put(`/api/employees/${employeeId}`, {
            vacationBalance: days,
          });
        }
        return Promise.resolve();
      });

      await Promise.all(updates);
      await fetchEmployees(); // Refresh list
      setIsVacationModalOpen(false);
      showToast("Vacation days updated successfully", "success");
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to save vacation days",
        "error",
      );
      console.error("Error saving vacation days:", err);
    }
  };

  const openVacationModal = () => {
    setIsVacationModalOpen(true);
  };

  const closeVacationModal = () => {
    setIsVacationModalOpen(false);
  };

  return (
    <PageWrapper
      title="Employees"
      subtitle="Manage your team members"
      actions={
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={openVacationModal}>
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">Manage Vacation Days</span>
          </Button>
          <Button size="sm" onClick={handleAddNew}>
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">Add Employee</span>
          </Button>
        </div>
      }
    >
      {error && (
        <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 rounded-xl">
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8 text-gray-500 dark:text-light-500">
          Loading employees...
        </div>
      ) : (
        <DataTable
          data={employeeList}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          emptyMessage="No employees found"
          currentUserId={auth?.employee?._id}
        />
      )}

      {/* Add/Edit Employee Modal */}
      <BaseModal
        isOpen={isOpen}
        onClose={closeModal}
        title={modalData ? "Edit Employee" : "Add New Employee"}
        subtitle={
          modalData ? "Update team member information" : "Add a new team member"
        }
        footer={
          <>
            <Button
              variant="bordered"
              onClick={closeModal}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting
                ? "Saving..."
                : modalData
                  ? "Update Employee"
                  : "Add Employee"}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {formErrors.submit && (
            <div className="p-3 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg text-sm">
              {formErrors.submit}
            </div>
          )}

          <Input
            label="Full Name (English)"
            placeholder="Enter full name"
            value={formData.fullNameEn}
            onChange={(e) => handleFormChange("fullNameEn", e.target.value)}
            error={formErrors.fullNameEn}
            required
          />
          <Input
            label="Full Name (Arabic)"
            placeholder="أدخل الاسم بالعربية"
            value={formData.fullNameAr}
            onChange={(e) => handleFormChange("fullNameAr", e.target.value)}
            error={formErrors.fullNameAr}
            required
          />
          <Input
            label="Nickname"
            placeholder="Enter nickname"
            value={formData.nickname}
            onChange={(e) => handleFormChange("nickname", e.target.value)}
            error={formErrors.nickname}
            required
          />
          <div>
            <Select
              label="Discord User"
              options={discordOptions}
              value={formData.discordId || NO_DISCORD}
              onChange={handleDiscordChange}
              error={formErrors.discordId}
            />
            {formData.discordId && (
              <p className="mt-1 text-xs text-gray-500 dark:text-light-200">
                Discord ID: {formData.discordId}
              </p>
            )}
            {discordError && <p className="mt-1 text-xs text-red-500">{discordError}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Employee Code"
              placeholder="Enter code"
              value={formData.employeeCode}
              onChange={(e) => handleFormChange("employeeCode", e.target.value)}
              error={formErrors.employeeCode}
              required
            />
            <Input
              label="Fingerprint Code"
              placeholder="Enter fingerprint code"
              value={formData.fingerprintCode}
              onChange={(e) =>
                handleFormChange("fingerprintCode", e.target.value)
              }
              error={formErrors.fingerprintCode}
              required
            />
          </div>
          <Input
            label="Email"
            type="email"
            placeholder="Enter email"
            value={formData.email}
            onChange={(e) => handleFormChange("email", e.target.value)}
            error={formErrors.email}
            required
            disabled={!!modalData} // Email cannot be changed on edit
          />
          <Input
            label="Phone"
            placeholder="Enter phone number"
            value={formData.phone}
            onChange={(e) => handleFormChange("phone", e.target.value)}
            error={formErrors.phone}
            required
          />
          {/* ── Multi-Team Assignments ──────────────────────────────────── */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-light-300">
                Teams <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={handleAddTeam}
                className="text-xs font-semibold text-primary-accent hover:text-orange-600 transition-colors flex items-center gap-1"
              >
                + Add Team
              </button>
            </div>
            {formData.teams.map((entry, idx) => (
              <div
                key={idx}
                className="flex items-end gap-2 p-3 bg-gray-50 dark:bg-dark-100 rounded-xl border border-gray-200 dark:border-dark-200"
              >
                <div className="flex-1">
                  <Select
                    label="Team"
                    options={[
                      { value: "high_potit", label: "High Potit" },
                      { value: "backend", label: "Backend" },
                      { value: "frontend", label: "Frontend" },
                      { value: "tester", label: "Tester" },
                      { value: "ra", label: "Requirement Analyst" },
                      { value: "mobile_app", label: "Mobile App" },
                      { value: "uiux", label: "UI/UX" },
                    ]}
                    value={entry.team}
                    onChange={(e) =>
                      handleTeamEntryChange(idx, "team", e.target.value)
                    }
                    required
                  />
                </div>
                <div className="flex-1">
                  <Select
                    label="Title"
                    options={[
                      { value: "team_leader", label: "Team Lead" },
                      { value: "senior", label: "Senior" },
                      { value: "mid_level", label: "Mid Level" },
                      { value: "junior", label: "Junior" },
                      { value: "intern", label: "Intern" },
                    ]}
                    value={entry.title}
                    onChange={(e) =>
                      handleTeamEntryChange(idx, "title", e.target.value)
                    }
                    required
                  />
                </div>
                {formData.teams.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveTeam(idx)}
                    className="mb-1 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors text-lg font-bold"
                    title="Remove this team"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {formErrors.teams && (
              <p className="text-sm text-red-500 dark:text-red-400">
                {formErrors.teams}
              </p>
            )}
          </div>
          {/* ─────────────────────────────────────────────────────────── */}
          <Input
            label="Branch"
            placeholder="Enter branch"
            value={formData.branch}
            onChange={(e) => handleFormChange("branch", e.target.value)}
            error={formErrors.branch}
            required
          />
        </form>
      </BaseModal>

      {/* Vacation Days Management Modal */}
      <BaseModal
        isOpen={isVacationModalOpen}
        onClose={closeVacationModal}
        title="Manage Vacation Days"
        subtitle="Set vacation days for each employee"
        size="xl"
        footer={
          <>
            <Button variant="bordered" onClick={closeVacationModal}>
              Cancel
            </Button>
            <Button onClick={handleSaveVacationDays}>Save Vacation Days</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Note:</strong> Enter the number of vacation days for each
              employee. This will be added to their vacation balance.
            </p>
          </div>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto scrollbar-thin">
            {employeeList.map((employee, index) => (
              <motion.div
                key={employee.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-dark-100 rounded-xl border border-gray-200 dark:border-dark-200 hover:border-primary-accent/50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-accent to-orange-600 flex items-center justify-center text-white font-bold text-sm">
                      {employee.fullName
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-light-100">
                        {employee.fullName}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-light-400">
                        {employee.teamsDisplay} • {employee.employeeCode}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="w-32">
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      value={vacationDays[employee.id] || 0}
                      onChange={(e) =>
                        handleVacationDaysChange(employee.id, e.target.value)
                      }
                      className="w-full px-4 py-2 bg-white dark:bg-primary-card border border-gray-300 dark:border-dark-200 rounded-lg text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all text-center font-semibold"
                      placeholder="0"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-light-500 pointer-events-none">
                      days
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {employeeList.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-light-500">
              No employees found
            </div>
          )}
        </div>
      </BaseModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Employee"
        message={
          employeeToDelete
            ? `Are you sure you want to permanently delete ${employeeToDelete.fullName}? This action cannot be undone.`
            : "Are you sure you want to permanently delete this employee?"
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </PageWrapper>
  );
};

export default Employees;
